import Image from "next/image";
import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for responsive sizing
 * @param {number} mobileSize - Size for mobile devices (width < 640px)
 * @param {number} desktopSize - Size for desktop devices (width >= 640px)
 * @returns {number} Current size based on screen width
 */
const useResponsiveSize = (mobileSize, desktopSize) => {
  const [size, setSize] = useState(mobileSize);
  
  useEffect(() => {
    const handleResize = () => {
      setSize(window.innerWidth >= 640 ? desktopSize : mobileSize); // 640px is Tailwind's sm breakpoint
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileSize, desktopSize]);
  
  return size;
};

/**
 * Enhanced Next.js Image component with loading states, responsive sizing, and full-width support
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL (required)
 * @param {string} props.alt - Alt text for accessibility (required)
 * @param {string} props.className - CSS classes for the wrapper div
 * @param {boolean} props.fullWidth - If true, image takes full width of container with auto height (like regular img)
 * @param {boolean} props.responsive - If true, uses responsive sizing based on screen width
 * @param {number} props.width - Fixed width (used when fullWidth=false and responsive=false)
 * @param {number} props.height - Fixed height (used when fullWidth=false and responsive=false)
 * @param {number} props.mobileWidth - Width for mobile devices (used when responsive=true)
 * @param {number} props.mobileHeight - Height for mobile devices (used when responsive=true)
 * @param {number} props.desktopWidth - Width for desktop devices (used when responsive=true)
 * @param {number} props.desktopHeight - Height for desktop devices (used when responsive=true)
 * @param {boolean} props.priority - If true, image loads with high priority (default: true)
 * @param {...any} props - All other props are passed to Next.js Image component
 * 
 * @example
 * // Full width image (like regular img element)
 * <NextImage
 *   src="/logo.png"
 *   alt="Logo"
 *   fullWidth={true}
 *   className="w-full h-auto"
 * />
 * 
 * @example
 * // Responsive image with different sizes for mobile/desktop
 * <NextImage
 *   src="/avatar.jpg"
 *   alt="Avatar"
 *   responsive={true}
 *   mobileWidth={80}
 *   mobileHeight={80}
 *   desktopWidth={112}
 *   desktopHeight={112}
 *   className="rounded-full"
 * />
 * 
 * @example
 * // Fixed size image
 * <NextImage
 *   src="/icon.png"
 *   alt="Icon"
 *   width={32}
 *   height={32}
 *   className="inline-block"
 * />
 */
export function NextImage({ 
  src, 
  width, 
  height, 
  alt, 
  className = "", 
  responsive = false,
  mobileWidth,
  mobileHeight,
  desktopWidth,
  desktopHeight,
  fullWidth = false,
  priority = true,
  ...props 
}) {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const imagesLoadedRef = useRef(false);

  // Use responsive sizing if enabled and dimensions are provided
  const responsiveWidth = useResponsiveSize(mobileWidth || width, desktopWidth || width);
  const responsiveHeight = useResponsiveSize(mobileHeight || height, desktopHeight || height);

  useEffect(() => {
    setImagesLoaded(imagesLoadedRef.current);
  }, []);

  const handleImageLoad = () => {
    imagesLoadedRef.current = true;
    setImagesLoaded(imagesLoadedRef.current);
  };

  // Don't render anything if there's no src
  if (!src) {
    return null;
  }

  // Determine image props based on mode
  let imageProps = {
    alt,
    className: `${!imagesLoaded && "opacity-0"}`,
    hidden: !src,
    onLoad: handleImageLoad,
    src,
    priority,
    ...props
  };

  if (fullWidth) {
    // Full width mode - behaves like regular img element
    imageProps = {
      ...imageProps,
      width: 0,
      height: 0,
      sizes: "100vw",
      style: {
        width: '100%',
        height: 'auto',
      }
    };
  } else if (responsive) {
    // Responsive mode - different sizes for mobile/desktop
    imageProps = {
      ...imageProps,
      width: responsiveWidth,
      height: responsiveHeight,
      style: {
        width: `${responsiveWidth}px`,
        height: `${responsiveHeight}px`,
        objectFit: 'cover',
      }
    };
  } else {
    // Fixed size mode
    imageProps = {
      ...imageProps,
      width,
      height
    };
  }

  return (
    <div className={`relative ${className}`}>
      {!imagesLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded-full">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer opacity-30"></div>
        </div>
      )}
      <Image {...imageProps} />
    </div>
  );
}
